import { Injectable, inject } from '@angular/core';
import { Firestore, collection, collectionData, doc, docData, updateDoc, setDoc, getDoc, getDocs, query, orderBy, serverTimestamp, addDoc } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CouponService } from './coupon.service';
import { findPhotoLavieBrand } from './coupon-brand.util';
import { toPacificDateString, todayInPacific } from '../utils/pacific-time.util';

export interface RefundRequest {
  id: string;
  ticketId?: string;
  requestorUid: string;
  requestorEmail: string;
  deviceId: string;
  brandId?: string | null;
  brandName?: string | null;
  sessionId?: string | null;
  sessionDate?: string | null;
  approximateTime?: string | null;
  storeLocation?: string | null;
  identificationMethod: 'qr_scan' | 'guided_match';
  reasonCategory: string;
  reasonText?: string;
  originalPaymentMethod: string;
  originalPaidAmount: number;
  eligibleCouponAmount: number;
  wantsCashRefund?: boolean;
  status: 'needs_review' | 'approved' | 'denied' | 'needs_more_info' | 'collecting' | 'completed';
  reviewReason?: string | null;
  riskScore?: number;
  riskBand?: 'low' | 'elevated' | 'needs_review' | 'high';
  riskFactors?: string[];
  aiSummary?: string;
  aiClassification?: string;
  aiConfidence?: number;
  evidenceSnapshot?: any;
  hasEvidence?: boolean;
  evidenceCount?: number;
  policyVersion?: string;
  createdAt?: any;
  requestedAt?: any;
  resolvedAt?: any;
  reviewedBy?: string | null;
  reviewedAt?: any;
  couponCode?: string | null;
  issuedCouponExpiry?: string;
  decision?: 'approve' | 'deny' | 'request_info' | null;
  decisionSource?: string | null;
  decisionRationale?: string | null;
  adminInquiry?: string | null;
  emailDeliveryStatus?: 'queued' | 'pending' | 'success' | 'failed' | null;
  emailReceiptId?: string | null;
  emailDeliveryError?: string | null;
  lastEmailRetriedAt?: any;
}

@Injectable({
  providedIn: 'root'
})
export class RefundManagementService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private couponService = inject(CouponService);
  private functions = inject(Functions, { optional: true });

  /**
   * Stream all refund requests in real-time ordered by newest first.
   */
  getRefundRequests(): Observable<RefundRequest[]> {
    const refundCol = collection(this.firestore, 'refund_requests');
    const q = query(refundCol, orderBy('createdAt', 'desc'));
    return collectionData(q, { idField: 'id' }) as Observable<RefundRequest[]>;
  }

  /**
   * Stream global refund management settings (auto-validation, deep log investigation, etc.).
   */
  getRefundSettings(): Observable<{
    autoValidationEnabled?: boolean;
    deepLogInvestigationEnabled?: boolean;
    deepLogAutoFetchKiosk?: boolean;
    investigationTimeoutSec?: number;
    customSemanticQuestions?: string[];
  }> {
    const configDoc = doc(this.firestore, 'photobooth_config', 'global');
    return (docData(configDoc) as Observable<any>).pipe(
      map(data => ({
        autoValidationEnabled: data ? data['autoRefundValidationEnabled'] !== false : true,
        deepLogInvestigationEnabled: data ? data['deepLogInvestigationEnabled'] === true : false,
        deepLogAutoFetchKiosk: data ? data['deepLogAutoFetchKiosk'] !== false : true,
        investigationTimeoutSec: data && typeof data['investigationTimeoutSec'] === 'number' ? data['investigationTimeoutSec'] : 20,
        customSemanticQuestions: data && Array.isArray(data['customSemanticQuestions']) ? data['customSemanticQuestions'] : []
      }))
    );
  }

  /**
   * Update refund management settings in global config.
   */
  async updateRefundSettings(settings: {
    autoValidationEnabled?: boolean;
    autoRefundValidationEnabled?: boolean;
    deepLogInvestigationEnabled?: boolean;
    deepLogAutoFetchKiosk?: boolean;
    investigationTimeoutSec?: number;
    customSemanticQuestions?: string[];
  }): Promise<void> {
    const configDoc = doc(this.firestore, 'photobooth_config', 'global');
    const updatePayload: any = {};
    if (settings.autoRefundValidationEnabled !== undefined) {
      updatePayload.autoRefundValidationEnabled = settings.autoRefundValidationEnabled;
    } else if (settings.autoValidationEnabled !== undefined) {
      updatePayload.autoRefundValidationEnabled = settings.autoValidationEnabled;
    }
    if (settings.deepLogInvestigationEnabled !== undefined) {
      updatePayload.deepLogInvestigationEnabled = settings.deepLogInvestigationEnabled;
    }
    if (settings.deepLogAutoFetchKiosk !== undefined) {
      updatePayload.deepLogAutoFetchKiosk = settings.deepLogAutoFetchKiosk;
    }
    if (settings.investigationTimeoutSec !== undefined) {
      updatePayload.investigationTimeoutSec = settings.investigationTimeoutSec;
    }
    if (settings.customSemanticQuestions !== undefined) {
      updatePayload.customSemanticQuestions = settings.customSemanticQuestions;
    }

    await updateDoc(configDoc, updatePayload);
  }

  /**
   * Brand scope of a refund coupon (KAN-227):
   * 1. Uses explicit request.brandId if already present and valid.
   * 2. Resolves the brand of the machine the session ran on (`devices/{id}.brandId`).
   * 3. Fallback: If deviceId is a store name, unregistered, or absent (common for User Portal refund requests),
   *    automatically resolves the default brand (K-Photo LaVie, case-insensitive, aliases: photo lavie, lavie).
   */
  private async resolveOriginatingBrand(deviceId?: string | null, requestBrandId?: string | null): Promise<{ brandId: string; brandName: string }> {
    // 1. If ticket already has a brandId, verify and use it directly
    const reqBrandId = (requestBrandId || '').trim();
    if (reqBrandId) {
      const brandSnap = await getDoc(doc(this.firestore, 'brands', reqBrandId));
      if (brandSnap.exists() && brandSnap.data()['isDeleted'] !== true) {
        return { brandId: reqBrandId, brandName: String(brandSnap.data()['name'] || reqBrandId) };
      }
    }

    // 2. Check if deviceId corresponds to an existing registered device
    const id = (deviceId || '').trim();
    if (id) {
      const deviceSnap = await getDoc(doc(this.firestore, 'devices', id));
      if (deviceSnap.exists()) {
        const brandId = String(deviceSnap.data()['brandId'] || '').trim();
        if (brandId) {
          const brandSnap = await getDoc(doc(this.firestore, 'brands', brandId));
          if (brandSnap.exists() && brandSnap.data()['isDeleted'] !== true) {
            return { brandId, brandName: String(brandSnap.data()['name'] || brandId) };
          }
        }
      }
    }

    // 3. Fallback: When created from user portal (store selected instead of machine), resolve default K-Photo LaVie brand
    const defaultBrand = await this.resolveDefaultBrand();
    if (defaultBrand) {
      return defaultBrand;
    }

    if (!id) {
      throw new Error('Cannot issue the coupon: the machine (device) of this session is unknown and the default brand could not be resolved.');
    }
    throw new Error(`Cannot issue the coupon: machine ${id} was not found, and brand could not be determined.`);
  }

  /**
   * Resolves the canonical K-Photo LaVie brand by name and alternate aliases.
   */
  private async resolveDefaultBrand(): Promise<{ brandId: string; brandName: string } | null> {
    try {
      const brandsCol = collection(this.firestore, 'brands');
      const brandsSnap = await getDocs(brandsCol);
      const allBrands = brandsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      return findPhotoLavieBrand(allBrands);
    } catch (e) {
      console.warn('[RefundManagementService] Could not resolve default brand:', e);
      return null;
    }
  }

  /**
   * Simple HTML escaping helper for email templates.
   */
  private escapeHtml(text?: string | null): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Helper to build fully compatible email_receipts payload consumed by Cloud Function sendEmailOnReceipt (KAN-344).
   */
  buildRefundCouponEmailPayload(params: {
    recipientEmail: string;
    couponCode: string;
    brandName: string;
    brandId: string;
    ticketId?: string;
    deviceId?: string;
    discountType: 'free_session' | 'fixed' | 'percent';
    discountValue: number;
    title: string;
    customNote?: string;
    senderUserEmail?: string;
  }): Record<string, any> {
    const rawCode = params.couponCode.trim().toUpperCase();
    const formattedCode = rawCode.length === 12
      ? `${rawCode.slice(0, 4)}-${rawCode.slice(4, 8)}-${rawCode.slice(8, 12)}`
      : rawCode;

    const discountDisplay = params.discountType === 'free_session'
      ? '100% FREE SESSION'
      : (params.discountType === 'percent'
        ? `${params.discountValue}% OFF`
        : `$${Number(params.discountValue || 0).toFixed(2)} OFF`);

    const safeBrandName = this.escapeHtml(params.brandName);
    const safeTitle = this.escapeHtml(params.title);
    const recipientName = params.recipientEmail.split('@')[0] || 'Valued Customer';
    const safeRecipientName = this.escapeHtml(recipientName);
    const fromName = `${safeBrandName} Support`;
    const subject = `Your ${params.brandName} Replacement Coupon Voucher`;

    // High-contrast, client-safe HTTPS QR code URL (identical to CouponService standard)
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(rawCode)}`;

    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px; background-color: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0; color: #1e293b;">
        <div style="text-align: center; margin-bottom: 24px;">
          <h1 style="color: #0f172a; margin: 0 0 8px 0; font-size: 24px; font-weight: 800;">${safeBrandName}</h1>
          <p style="color: #64748b; margin: 0; font-size: 14px;">Replacement Voucher Confirmation</p>
        </div>

        <div style="background: white; border-radius: 10px; padding: 24px; text-align: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
          <p style="font-size: 16px; color: #334155; margin-top: 0;">Hello ${safeRecipientName},</p>
          <div style="font-size: 14px; color: #475569; margin: 12px 0; line-height: 1.6;">
            We have approved your refund request. Please find your replacement voucher coupon code below, valid across all ${safeBrandName} photo booth kiosks.
          </div>

          <div style="margin: 20px 0; padding: 16px; background-color: #f0fdf4; border: 1px dashed #22c55e; border-radius: 8px;">
            <div style="font-size: 28px; font-weight: 800; color: #15803d; letter-spacing: 0.5px;">${discountDisplay}</div>
            <div style="font-size: 14px; font-weight: 600; color: #166534; margin-top: 4px;">${safeTitle}</div>
          </div>

          <div style="margin 24px 0;">
            <img src="${qrImageUrl}" alt="Coupon QR Code" width="200" height="200" style="width: 200px; height: 200px; display: inline-block; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; background: white;" />
            <div style="margin-top: 12px; font-family: monospace; font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: 2px;">
              ${formattedCode}
            </div>
          </div>

          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #f1f5f9; font-size: 13px; color: #475569; line-height: 1.6; text-align: left;">
            <div><strong>Valid For:</strong> All ${safeBrandName} locations</div>
            <div><strong>Usage:</strong> Single Use Only</div>
            <div><strong>Expiration:</strong> No Expiration</div>
          </div>

          <div style="margin-top: 20px; padding: 16px; background-color: #f8fafc; border-radius: 8px; text-align: left;">
            <div style="font-size: 13px; font-weight: 700; color: #334155; margin-bottom: 8px; text-transform: uppercase;">How to Redeem at Kiosk:</div>
            <ol style="margin: 0; padding-left: 20px; font-size: 13px; color: #475569; line-height: 1.6;">
              <li>Start a new session at any ${safeBrandName} photobooth.</li>
              <li>On the payment screen, tap <strong>Coupon / Voucher</strong>.</li>
              <li>Scan this QR code or manually enter the code shown above.</li>
            </ol>
          </div>
        </div>

        <div style="text-align: center; margin-top: 24px; color: #94a3b8; font-size: 12px;">
          <p style="margin: 0 0 4px 0;">Ticket Reference: ${this.escapeHtml(params.ticketId || 'Staff Manual')} • Device: ${this.escapeHtml(params.deviceId || 'N/A')}</p>
          <p style="margin: 0;">© ${safeBrandName}. All rights reserved.</p>
        </div>
      </div>
    `;

    const textContent = `${params.brandName} - Replacement Voucher Confirmation\n\n` +
      `Hello ${recipientName},\n\n` +
      `We have approved your refund request. Your replacement voucher coupon code is:\n\n` +
      `${formattedCode}\n\n` +
      `Discount: ${discountDisplay}\n` +
      `Title: ${params.title}\n` +
      `Valid For: All ${params.brandName} locations (Single Use Only)\n\n` +
      `How to Redeem:\n` +
      `1. Start a session at any ${params.brandName} photobooth.\n` +
      `2. On the payment screen, tap "Coupon / Voucher".\n` +
      `3. Scan your QR code or manually enter the code above.\n\n` +
      `Ticket Reference: ${params.ticketId || 'Staff Manual'} | Device: ${params.deviceId || 'N/A'}`;

    return {
      senderType: 'coupon',
      fromName: fromName,
      to: [params.recipientEmail.trim().toLowerCase()],
      recipientEmail: params.recipientEmail.trim().toLowerCase(),
      recipientName: recipientName,
      subject: subject,
      message: {
        subject: subject,
        html: htmlContent,
        text: textContent
      },
      html: htmlContent,
      text: textContent,
      couponCode: rawCode,
      couponTitle: params.title,
      discountType: params.discountType,
      discountValue: params.discountValue,
      applicableBrands: [params.brandId],
      brandName: params.brandName,
      customSubject: subject,
      customMessage: params.customNote || `We have approved your refund request. Please find your replacement voucher coupon code below.`,
      status: 'pending',
      deliveryStatus: 'PENDING',
      ticketId: params.ticketId || null,
      sentBy: params.senderUserEmail || 'staff_action'
    };
  }

  /**
   * Approve a refund request: generates a single-use coupon and marks request as approved.
   */
  async approveRefund(
    request: RefundRequest,
    staffEmail: string,
    options?: {
      couponTitle?: string;
      discountType?: 'free_session' | 'fixed' | 'percent';
      discountValue?: number;
      notes?: string;
    }
  ): Promise<string> {
    // 0. Recipient Email Validation (AC-02 Fail-Closed Invariant)
    const rawEmail = (request.requestorEmail || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!rawEmail || !emailRegex.test(rawEmail)) {
      throw new Error(`Cannot approve refund: Customer email "${request.requestorEmail || ''}" is invalid or missing.`);
    }

    // Resolve the brand first: nothing is written when the originating brand cannot be determined.
    const brand = await this.resolveOriginatingBrand(request.deviceId || request.evidenceSnapshot?.deviceId, request.brandId);
    const couponCode = this.couponService.generateCouponCode();
    const nowIso = new Date().toISOString();
    const discountType = options?.discountType || 'free_session';
    const discountValue = options?.discountValue ?? (request.eligibleCouponAmount || 10.0);
    const title = options?.couponTitle || `Refund Replacement (${request.deviceId || 'PhotoLavie'})`;

    // 1. Commit new single-use coupon
    const couponRef = doc(this.firestore, 'coupons', couponCode);
    await setDoc(couponRef, {
      code: couponCode,
      title: title,
      discountType: discountType,
      discountValue: discountValue,
      applicableStores: ['all'],
      applicableBrands: [brand.brandId],
      recipientName: rawEmail.split('@')[0] || 'Valued Customer',
      recipientEmail: rawEmail,
      issuedBy: staffEmail,
      issuedAt: nowIso,
      expiresAt: null,
      usageType: 'single_use',
      maxRedemptions: 1,
      timesRedeemed: 0,
      status: 'active',
      isActive: true,
      isDeleted: false
    });

    // 2. Update refund request
    const requestRef = doc(this.firestore, 'refund_requests', request.id);
    await updateDoc(requestRef, {
      status: 'approved',
      resolvedAt: serverTimestamp(),
      reviewedBy: staffEmail,
      reviewedAt: serverTimestamp(),
      couponCode: couponCode,
      decision: 'approve',
      decisionSource: 'staff_manual',
      decisionRationale: options?.notes || 'Approved by staff review',
      emailDeliveryStatus: 'queued'
    });

    // 3. Queue customer email receipt notification with complete compatible payload
    try {
      const emailPayload = this.buildRefundCouponEmailPayload({
        recipientEmail: rawEmail,
        couponCode: couponCode,
        brandName: brand.brandName,
        brandId: brand.brandId,
        ticketId: request.ticketId || request.id,
        deviceId: request.deviceId,
        discountType: discountType,
        discountValue: discountValue,
        title: title,
        customNote: options?.notes,
        senderUserEmail: staffEmail
      });

      const emailCol = collection(this.firestore, 'email_receipts');
      const emailDocRef = await addDoc(emailCol, {
        ...emailPayload,
        createdAt: serverTimestamp()
      });

      await updateDoc(requestRef, {
        emailReceiptId: emailDocRef.id,
        emailDeliveryStatus: 'pending'
      });
    } catch (e: any) {
      console.warn('Could not queue email receipt for approved refund:', e);
      await updateDoc(requestRef, {
        emailDeliveryStatus: 'failed',
        emailDeliveryError: e?.message || String(e)
      });
    }

    return couponCode;
  }

  /**
   * Retry email dispatch for an already-approved refund request without creating duplicate coupons (AC-05).
   */
  async retryCouponEmail(request: RefundRequest, staffEmail: string): Promise<void> {
    const rawEmail = (request.requestorEmail || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!rawEmail || !emailRegex.test(rawEmail)) {
      throw new Error(`Cannot retry email: Customer email "${request.requestorEmail || ''}" is invalid or missing.`);
    }

    if (!request.couponCode) {
      throw new Error('Cannot retry email: No coupon code has been issued for this refund request.');
    }

    const brand = await this.resolveOriginatingBrand(request.deviceId || request.evidenceSnapshot?.deviceId, request.brandId);
    const emailPayload = this.buildRefundCouponEmailPayload({
      recipientEmail: rawEmail,
      couponCode: request.couponCode,
      brandName: brand.brandName,
      brandId: brand.brandId,
      ticketId: request.ticketId || request.id,
      deviceId: request.deviceId,
      discountType: 'free_session',
      discountValue: request.eligibleCouponAmount || request.originalPaidAmount || 10.0,
      title: `Refund Replacement (${request.deviceId || 'PhotoLavie'})`,
      senderUserEmail: staffEmail
    });

    const emailCol = collection(this.firestore, 'email_receipts');
    const emailDocRef = await addDoc(emailCol, {
      ...emailPayload,
      isRetry: true,
      retriedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    const requestRef = doc(this.firestore, 'refund_requests', request.id);
    await updateDoc(requestRef, {
      emailReceiptId: emailDocRef.id,
      emailDeliveryStatus: 'pending',
      lastEmailRetriedAt: serverTimestamp(),
      emailDeliveryError: null
    });
  }

  /**
   * Deny a refund request with mandatory staff rationale.
   */
  async denyRefund(request: RefundRequest, staffEmail: string, rationale: string): Promise<void> {
    const requestRef = doc(this.firestore, 'refund_requests', request.id);
    await updateDoc(requestRef, {
      status: 'denied',
      resolvedAt: serverTimestamp(),
      reviewedBy: staffEmail,
      reviewedAt: serverTimestamp(),
      decision: 'deny',
      decisionSource: 'staff_manual',
      decisionRationale: rationale || 'Denied by staff review'
    });
  }

  /**
   * Mark request as needing additional information.
   */
  async requestMoreInfo(request: RefundRequest, staffEmail: string, inquiry: string): Promise<void> {
    const requestRef = doc(this.firestore, 'refund_requests', request.id);
    await updateDoc(requestRef, {
      status: 'needs_more_info',
      reviewedBy: staffEmail,
      reviewedAt: serverTimestamp(),
      decision: 'request_info',
      adminInquiry: inquiry
    });
  }

  /**
   * Generates a user-friendly ticket ID in the format RF-XXX-XXX-X (e.g. RF-834-291-5)
   */
  generateRefundTicketId(): string {
    const p1 = Math.floor(100 + Math.random() * 900);
    const p2 = Math.floor(100 + Math.random() * 900);
    const p3 = Math.floor(Math.random() * 10);
    return `RF-${p1}-${p2}-${p3}`;
  }

  /**
   * Manually create a refund request by staff with option to auto-approve & issue coupon immediately.
   */
  async createManualRefund(
    data: {
      requestorEmail: string;
      brandId?: string;
      brandName?: string;
      deviceId?: string;
      storeLocation?: string;
      sessionId?: string;
      sessionDate?: string;
      approximateTime?: string;
      originalPaymentMethod?: string;
      originalPaidAmount: number;
      eligibleCouponAmount?: number;
      wantsCashRefund?: boolean;
      reasonCategory: string;
      reasonText?: string;
      autoApprove?: boolean;
      couponTitle?: string;
      discountType?: 'free_session' | 'fixed' | 'percent';
      discountValue?: number;
      notes?: string;
    },
    staffEmail: string
  ): Promise<{ requestId: string; couponCode?: string }> {
    // 0. Recipient Email Validation (Fail-Closed)
    const rawEmail = (data.requestorEmail || '').trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!rawEmail || !emailRegex.test(rawEmail)) {
      throw new Error(`Cannot create manual refund: Customer email "${data.requestorEmail || ''}" is invalid or missing.`);
    }

    const nowIso = new Date().toISOString();
    const isCash = !!data.wantsCashRefund;
    const isAutoApprove = !isCash && !!data.autoApprove;
    let couponCode: string | undefined;
    let brand: { brandId: string; brandName: string } | undefined;

    if (isAutoApprove) {
      // The brand comes from the machine of the session; without it nothing is issued or written.
      brand = await this.resolveOriginatingBrand(data.deviceId, (data as any).brandId);
      couponCode = this.couponService.generateCouponCode();
      const discountType = data.discountType || 'free_session';
      const discountValue = data.discountValue ?? (data.eligibleCouponAmount || data.originalPaidAmount || 10.0);
      const title = data.couponTitle || `Refund Replacement (${data.deviceId || data.storeLocation || 'PhotoLavie'})`;

      // 1. Create Coupon Document
      const couponRef = doc(this.firestore, 'coupons', couponCode);
      await setDoc(couponRef, {
        code: couponCode,
        title: title,
        discountType: discountType,
        discountValue: discountValue,
        applicableStores: ['all'],
        applicableBrands: [brand.brandId],
        recipientName: rawEmail.split('@')[0] || 'Valued Customer',
        recipientEmail: rawEmail,
        issuedBy: staffEmail,
        issuedAt: nowIso,
        expiresAt: null,
        usageType: 'single_use',
        maxRedemptions: 1,
        timesRedeemed: 0,
        status: 'active',
        isActive: true,
        isDeleted: false
      });

      // 2. Queue Email Receipt with full compatible payload
      try {
        const emailPayload = this.buildRefundCouponEmailPayload({
          recipientEmail: rawEmail,
          couponCode: couponCode,
          brandName: brand.brandName,
          brandId: brand.brandId,
          ticketId: 'Manual Staff Instant Approval',
          deviceId: data.deviceId,
          discountType: discountType,
          discountValue: discountValue,
          title: title,
          customNote: data.notes,
          senderUserEmail: staffEmail
        });

        const emailCol = collection(this.firestore, 'email_receipts');
        await addDoc(emailCol, {
          ...emailPayload,
          createdAt: serverTimestamp()
        });
      } catch (e) {
        console.warn('Could not queue email receipt for manual refund:', e);
      }
    }

    // Create Refund Request Document with RF-XXX-XXX-X ID
    const ticketId = this.generateRefundTicketId();
    const refundDocRef = doc(this.firestore, 'refund_requests', ticketId);
    await setDoc(refundDocRef, {
      ticketId: ticketId,
      requestorUid: 'staff_manual_entry',
      requestorEmail: data.requestorEmail.trim().toLowerCase(),
      deviceId: data.deviceId || '',
      brandId: brand?.brandId || (data as any).brandId || null,
      brandName: brand?.brandName || (data as any).brandName || null,
      storeLocation: data.storeLocation || '',
      sessionId: data.sessionId || null,
      sessionDate: toPacificDateString(data.sessionDate) || todayInPacific(),   // Pacific calendar date (PT), YYYY-MM-DD
      approximateTime: data.approximateTime || null,
      identificationMethod: 'manual_entry',
      reasonCategory: data.reasonCategory || 'Manual Staff Refund',
      reasonText: data.reasonText || '',
      originalPaymentMethod: data.originalPaymentMethod || 'Credit Card',
      originalPaidAmount: Number(data.originalPaidAmount) || 10.0,
      eligibleCouponAmount: Number(data.eligibleCouponAmount) || Number(data.originalPaidAmount) || 10.0,
      wantsCashRefund: isCash,
      status: isAutoApprove ? 'approved' : 'needs_review',
      reviewReason: isCash ? 'Staff flagged cash refund request (requires manual banking review)' : null,
      createdAt: serverTimestamp(),
      requestedAt: serverTimestamp(),
      createdBy: staffEmail,
      reviewedBy: isAutoApprove ? staffEmail : null,
      reviewedAt: isAutoApprove ? serverTimestamp() : null,
      resolvedAt: isAutoApprove ? serverTimestamp() : null,
      couponCode: couponCode || null,
      decision: isAutoApprove ? 'approve' : null,
      decisionSource: 'staff_manual',
      decisionRationale: data.notes || (isAutoApprove ? 'Manually created and approved by staff' : null)
    });

    return { requestId: ticketId, couponCode };
  }

  /**
   * Stream all photo evidence manifests attached to a refund request (KAN-24).
   */
  getRefundEvidence(requestId: string): Observable<any[]> {
    const evidenceCol = collection(this.firestore, 'refund_requests', requestId, 'evidence');
    return collectionData(evidenceCol, { idField: 'id' });
  }

  /**
   * Securely retrieve short-lived view URL for an evidence attachment.
   * This invokes the backend callable which enforces staff reviewer permissions and audits the review access.
   */
  async getEvidenceViewUrl(requestId: string, attachmentId: string, type: 'thumbnail' | 'full' = 'full'): Promise<{
    downloadUrl: string;
    expiresAt: string;
    dimensions?: { width: number; height: number };
    sanitizedMimeType?: string;
    byteCount?: number;
    sha256Hash?: string;
  }> {
    if (!this.functions) {
      throw new Error('Firebase Functions is not initialized.');
    }

    const callable = httpsCallable<{ requestId: string; attachmentId: string; type: string }, any>(
      this.functions,
      'getRefundEvidenceUrl'
    );
    const result = await callable({ requestId, attachmentId, type });
    return result.data;
  }

}

